import React, { useEffect, useState } from 'react';
import { Flex, Loader, Text } from '@vibe/core';
import { format, parseISO } from 'date-fns';
import { getWorklogs, type Worklog } from '../../api/worklog.api';
import { fetchItemsByIds, fetchBoardsByIds } from '../../api/monday.api';
import { formatHoursMinutes } from '../../utils/timesheet-capacity';

export interface TimesheetWorklogListProps {
  userId: string;
  periodStart: string;
  periodEnd: string;
}

/**
 * Displays the individual worklog entries for a timesheet submission
 * so the approver can see what they're approving.
 * Resolves item and board names from monday.com.
 */
const TimesheetWorklogList: React.FC<TimesheetWorklogListProps> = ({
  userId,
  periodStart,
  periodEnd,
}) => {
  const [worklogs, setWorklogs] = useState<Worklog[]>([]);
  const [loading, setLoading] = useState(true);
  const [itemNames, setItemNames] = useState<Record<string, string>>({});
  const [boardNames, setBoardNames] = useState<Record<string, string>>({});

  // Fetch worklogs
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    getWorklogs({ userId, startDate: periodStart, endDate: periodEnd })
      .then((data) => {
        if (!cancelled) setWorklogs(data);
      })
      .catch(() => {
        if (!cancelled) setWorklogs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, periodStart, periodEnd]);

  // Resolve item and board names once worklogs are loaded
  useEffect(() => {
    if (worklogs.length === 0) return;

    let cancelled = false;

    const uniqueItemIds = [...new Set(worklogs.map((w) => w.mondayItemId))];
    const uniqueBoardIds = [...new Set(worklogs.map((w) => w.mondayBoardId))];

    Promise.all([
      fetchItemsByIds(uniqueItemIds),
      fetchBoardsByIds(uniqueBoardIds),
    ])
      .then(([items, boards]) => {
        if (cancelled) return;
        const iMap: Record<string, string> = {};
        for (const i of items) iMap[i.id] = i.name;
        setItemNames(iMap);

        const bMap: Record<string, string> = {};
        for (const b of boards) bMap[b.id] = b.name;
        setBoardNames(bMap);
      })
      .catch(() => {
        // Silently ignore — we'll show IDs as fallback
      });

    return () => {
      cancelled = true;
    };
  }, [worklogs]);

  if (loading) {
    return (
      <div className="rp-ts-worklogs__loading">
        <Loader size={Loader.sizes.SMALL} />
      </div>
    );
  }

  if (worklogs.length === 0) {
    return (
      <div className="rp-ts-worklogs rp-ts-worklogs--empty">
        <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
          No worklogs found for this period.
        </Text>
      </div>
    );
  }

  // Group worklogs by date
  const grouped = worklogs.reduce<Record<string, Worklog[]>>((acc, wl) => {
    const key = wl.date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(wl);
    return acc;
  }, {});

  const sortedDates = Object.keys(grouped).sort();

  // Category breakdown summary
  const categoryBreakdown = worklogs.reduce<
    Record<string, { name: string; color: string; hours: number; isBillable: boolean | null }>
  >((acc, wl) => {
    const key = wl.worklogCategoryName ?? '__uncategorized__';
    if (!acc[key]) {
      acc[key] = {
        name: wl.worklogCategoryName ?? 'Uncategorized',
        color: wl.worklogCategoryColor ?? '#808080',
        hours: 0,
        isBillable: wl.worklogCategoryIsBillable,
      };
    }
    acc[key].hours += Number(wl.hours);
    return acc;
  }, {});

  const sortedCategories = Object.values(categoryBreakdown).sort(
    (a, b) => b.hours - a.hours,
  );

  return (
    <div className="rp-ts-worklogs">
      <div className="rp-ts-worklogs__header">
        <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
          Worklog Details
        </Text>
        <span className="rp-ts-worklogs__count">{worklogs.length} entries</span>
      </div>

      {/* Category breakdown */}
      {sortedCategories.length > 0 && (
        <div className="rp-ts-worklogs__category-breakdown">
          <Text type={Text.types.TEXT2} weight={Text.weights.BOLD} style={{ marginBottom: 4 }}>
            Category Breakdown
          </Text>
          <div className="rp-ts-worklogs__category-list">
            {sortedCategories.map((cat) => (
              <Flex
                key={cat.name}
                align={Flex.align.CENTER}
                gap={Flex.gaps.XS}
                className="rp-ts-worklogs__category-row"
              >
                <span
                  className="rp-ts-worklogs__category-dot"
                  style={{ backgroundColor: cat.color }}
                />
                <Text type={Text.types.TEXT2} ellipsis style={{ flex: 1 }}>
                  {cat.name}
                  {cat.isBillable === true && (
                    <span style={{ color: '#00854d', marginLeft: 4, fontSize: '0.85em' }}>
                      (billable)
                    </span>
                  )}
                </Text>
                <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
                  {formatHoursMinutes(cat.hours)}
                </Text>
              </Flex>
            ))}
          </div>
        </div>
      )}

      <div className="rp-ts-worklogs__list">
        {sortedDates.map((date) => {
          const dayWorklogs = grouped[date];
          const dayTotal = dayWorklogs.reduce((s, w) => s + Number(w.hours), 0);

          return (
            <div key={date} className="rp-ts-worklogs__day">
              <Flex
                justify={Flex.justify.SPACE_BETWEEN}
                align={Flex.align.CENTER}
                className="rp-ts-worklogs__day-header"
              >
                <Text type={Text.types.TEXT2} weight={Text.weights.BOLD}>
                  {format(parseISO(date), 'EEEE, MMM d')}
                </Text>
                <span className="rp-ts-worklogs__day-total">
                  {formatHoursMinutes(dayTotal)}
                </span>
              </Flex>

              {dayWorklogs.map((wl) => (
                <div key={wl.id} className="rp-ts-worklogs__entry">
                  <Flex
                    justify={Flex.justify.SPACE_BETWEEN}
                    align={Flex.align.CENTER}
                  >
                    <div className="rp-ts-worklogs__entry-info">
                      <Text
                        type={Text.types.TEXT2}
                        weight={Text.weights.MEDIUM}
                        ellipsis
                      >
                        {itemNames[wl.mondayItemId] ?? `Item #${wl.mondayItemId}`}
                      </Text>
                      <Text
                        type={Text.types.TEXT2}
                        color={Text.colors.SECONDARY}
                        ellipsis
                        className="rp-ts-worklogs__entry-board"
                      >
                        {boardNames[wl.mondayBoardId] ?? `Board #${wl.mondayBoardId}`}
                      </Text>
                      {wl.worklogCategoryName && (
                        <span className="rp-ts-worklogs__entry-category">
                          <span
                            className="rp-ts-worklogs__category-dot"
                            style={{ backgroundColor: wl.worklogCategoryColor ?? '#808080' }}
                          />
                          <Text
                            type={Text.types.TEXT2}
                            color={Text.colors.SECONDARY}
                            ellipsis
                          >
                            {wl.worklogCategoryName}
                          </Text>
                        </span>
                      )}
                    </div>
                    <span className="rp-ts-worklogs__entry-hours">
                      {formatHoursMinutes(Number(wl.hours))}
                    </span>
                  </Flex>
                  {wl.description && (
                    <Text
                      type={Text.types.TEXT2}
                      color={Text.colors.SECONDARY}
                      className="rp-ts-worklogs__entry-desc"
                    >
                      {wl.description}
                    </Text>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TimesheetWorklogList;
